/**
 * Arena features stepped by the shared simulation (Phase 8): hazards hurt or heat a mech
 * standing in them, pickups restore shield or dump heat and respawn on a timer, boost pads
 * launch, and concealment zones hide. Everything static comes from the sidecar through
 * `MatchStatic.arena.features`; the hashed state carries only what changes
 * (`MatchState.features`: pickup respawn ticks and per-slot pad cooldowns). Pickups are decided
 * by the server alone (never inside a client prediction) so a taken cell never flickers back;
 * hazard heat and pad launches are predicted like every other movement rule.
 */
import { concealmentAt, pointInYawBox } from '../arena/features.js';
import { platformPoseAt } from '../arena/platforms.js';
import { SIM_DT } from '../constants.js';
import { applyDamage, kill } from './damage.js';
import { addEffect } from './effects.js';
import { sphereCapsuleOverlap } from './geometry.js';
import { addHeat, removeHeat } from './resources.js';
import { centerOf, combatantCapsule, secondsToTicks } from './state.js';
import { defOf, emit, isLive, mech, predicting } from './tick.js';
/** Floor features are tested half a metre above the feet, well inside a floor-level volume. */
export const FEATURE_PROBE_HEIGHT = 0.5;
const probe = { x: 0, y: 0, z: 0 };
function probeAt(slotPosition) {
    probe.x = slotPosition.x;
    probe.y = slotPosition.y + FEATURE_PROBE_HEIGHT;
    probe.z = slotPosition.z;
    return probe;
}
function stepHazards(t, slot) {
    const c = mech(t, slot);
    const p = probeAt(c.body.position);
    for (const zone of t.ctx.arena.features.hazards) {
        if (!c.alive)
            return;
        if (!pointInYawBox(p, zone.box))
            continue;
        if (zone.instantKill) {
            // A kill decides the round: the server's call, like every other death.
            if (!predicting(t))
                kill(t, slot, null);
            return;
        }
        if (zone.heatPerSecond > 0)
            addHeat(t, slot, zone.heatPerSecond * SIM_DT);
        if (zone.damagePerSecond > 0) {
            applyDamage(t, {
                target: slot,
                source: slot,
                amount: zone.damagePerSecond * SIM_DT,
                kind: 'hazard',
                location: 'torso',
                rear: false,
                position: centerOf(c.body, defOf(t, slot)),
            });
        }
    }
}
function stepPads(t, slot) {
    const c = mech(t, slot);
    if (!c.alive)
        return;
    const pads = t.ctx.arena.features.boostPads;
    const runtime = t.s.features.pads;
    const p = probeAt(c.body.position);
    for (let i = 0; i < pads.length; i++) {
        const pad = pads[i];
        const rt = runtime[i];
        if (!pad || !rt || t.s.tick < rt.readyTick[slot])
            continue;
        if (!pointInYawBox(p, pad.box))
            continue;
        rt.readyTick[slot] = t.s.tick + secondsToTicks(pad.cooldownSeconds);
        const yaw = pad.box.yaw;
        // Forward at yaw 0 is -Z; the launch rides the ordinary impulse path into the next step.
        addEffect(t, slot, 'padLaunch', slot, 1, {
            impulse: {
                x: -Math.sin(yaw) * pad.launch.forward,
                y: pad.launch.up,
                z: -Math.cos(yaw) * pad.launch.forward,
            },
        });
        emit(t, { t: 'padLaunch', slot, id: pad.id });
    }
}
function takePickup(t, slot, pickup, slotState) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    if (pickup.kind === 'shieldCell') {
        c.shield = Math.min(def.shield, c.shield + pickup.amount);
    }
    else {
        removeHeat(t, slot, pickup.amount);
        // A heat sink also ends an overheat lockout in progress.
        c.overheatTicks = 0;
    }
    slotState.respawnTick = t.s.tick + secondsToTicks(pickup.respawnSeconds);
    emit(t, { t: 'pickup', slot, id: pickup.id, kind: pickup.kind });
}
function stepPickups(t) {
    if (predicting(t))
        return;
    const pickups = t.ctx.arena.features.pickups;
    const runtime = t.s.features.pickups;
    for (let i = 0; i < pickups.length; i++) {
        const pickup = pickups[i];
        const rt = runtime[i];
        if (!pickup || !rt || t.s.tick < rt.respawnTick)
            continue;
        const at = { x: pickup.position[0], y: pickup.position[1], z: pickup.position[2] };
        // Slot 0 first on the same tick; spawns swap every round, so the edge evens out.
        for (const slot of [0, 1]) {
            const c = mech(t, slot);
            if (!c.alive)
                continue;
            if (!sphereCapsuleOverlap(at, pickup.radius, combatantCapsule(c, defOf(t, slot))))
                continue;
            takePickup(t, slot, pickup, rt);
            break;
        }
    }
}
/**
 * Steps the arena features after movement and before combat: hazards and pads per slot in slot
 * order, then pickups. Nothing happens outside live play (a winner standing in lava during the
 * round-end banner is not burned).
 */
export function stepArena(t) {
    if (!isLive(t))
        return;
    const features = t.ctx.arena.features;
    if (features.hazards.length > 0)
        for (const slot of [0, 1])
            stepHazards(t, slot);
    if (features.boostPads.length > 0)
        for (const slot of [0, 1])
            stepPads(t, slot);
    if (features.pickups.length > 0)
        stepPickups(t);
}
// --- moving platforms ---------------------------------------------------------------------------
/** Blocker ids of platforms: the prefix plus the platform's index in the sidecar list. */
export const PLATFORM_BLOCKER_PREFIX = 'platform:';
const poseNow = { center: { x: 0, y: 0, z: 0 }, yaw: 0 };
const poseBefore = { center: { x: 0, y: 0, z: 0 }, yaw: 0 };
/** Appends one blocker per platform at the pose it holds at `tick`. */
export function platformBlockers(features, tick, out) {
    for (let i = 0; i < features.platforms.length; i++) {
        const p = features.platforms[i];
        if (!p)
            continue;
        const pose = platformPoseAt(p, tick, poseNow);
        out.push({
            id: `${PLATFORM_BLOCKER_PREFIX}${String(i)}`,
            center: { x: pose.center.x, y: pose.center.y, z: pose.center.z },
            halfExtents: { x: p.halfExtents[0], y: p.halfExtents[1], z: p.halfExtents[2] },
            yaw: pose.yaw,
        });
    }
}
/**
 * Before movement: a mech standing on a platform moves with it by the platform's delta since
 * the last tick, so it lands exactly on the platform's new pose (already synced into the world).
 */
export function carryOnPlatforms(t) {
    const platforms = t.ctx.arena.features.platforms;
    if (platforms.length === 0)
        return;
    for (const slot of [0, 1]) {
        const c = mech(t, slot);
        if (!c.alive || c.platform === null)
            continue;
        const p = platforms[c.platform];
        if (!p) {
            c.platform = null;
            continue;
        }
        const now = platformPoseAt(p, t.s.tick, poseNow);
        const before = platformPoseAt(p, t.s.tick - 1, poseBefore);
        c.body.position.x += now.center.x - before.center.x;
        c.body.position.y += now.center.y - before.center.y;
        c.body.position.z += now.center.z - before.center.z;
    }
}
const down = { x: 0, y: -1, z: 0 };
const PLATFORM_PROBE_UP = 0.2;
const PLATFORM_PROBE_LENGTH = 0.6;
/** After movement: which platform, if any, each grounded mech is standing on. */
export function stepPlatformContact(t) {
    if (t.ctx.arena.features.platforms.length === 0)
        return;
    for (const slot of [0, 1]) {
        const c = mech(t, slot);
        if (!c.alive || !c.body.grounded) {
            c.platform = null;
            continue;
        }
        const origin = {
            x: c.body.position.x,
            y: c.body.position.y + PLATFORM_PROBE_UP,
            z: c.body.position.z,
        };
        const hit = t.world.castRayHit(origin, down, PLATFORM_PROBE_LENGTH);
        const id = hit?.blockerId;
        c.platform =
            id !== null && id !== undefined && id.startsWith(PLATFORM_BLOCKER_PREFIX)
                ? Number(id.slice(PLATFORM_BLOCKER_PREFIX.length))
                : null;
    }
}
/** The concealment zone a mech's torso is inside, or null (for the bot, the radar, the nameplate). */
export function concealmentOf(features, c, def) {
    if (features.concealment.length === 0)
        return null;
    return concealmentAt(features, centerOf(c.body, def));
}
//# sourceMappingURL=arena.js.map